import {Menu, MenuProps} from '@mui/material';
import {ReactNode, useEffect, useState} from 'react';

import {SxProps} from '~constants/theme';

import {usePopupHandler} from 'src/hooks/misc.hooks';
import {TooltipProps} from '../Tooltip';
import {ToolbarElementProps} from './toolbar.utils';
import {ToolbarButton} from './ToolbarButton';
import {ToolbarIconButton} from './ToolbarIconButton';

/** Gap between the anchor and the menu, applied via `marginTop: 2` on the Menu below. */
const MENU_OFFSET_PX = 16;
/** MUI Popover's default `marginThreshold`: the minimum distance the paper keeps to the viewport edges. */
const POPOVER_MARGIN_THRESHOLD_PX = 16;
/**
 * Never cap the menu below this height: anything smaller would not even fit the search field and a single item.
 * When less space is left below the anchor (a toolbar near the bottom of a very short window, or the main
 * menu at the bottom of the sidebar), the menu is left uncapped and MUI's default behavior of shifting it
 * upwards is the accepted fallback.
 */
const MENU_MIN_MAX_HEIGHT_PX = 240;

/**
 * Max height (in px) a toolbar menu may take so that it always fits below its anchor, or undefined when
 * there is not enough room below to be worth capping. MUI Popover shifts a paper upwards when it would
 * overflow the viewport bottom, which makes tall menus (e.g. filters with many values) cover the toolbar.
 * Capping the height instead keeps the menu anchored and lets its list scroll.
 *
 * Returning undefined matters for menus anchored near the bottom of the viewport (the main menu's "More"):
 * capping those to the minimum clips the last items instead, and a paper with `overflow: hidden` cannot
 * even scroll to them. There MUI's upward shift is the accepted fallback.
 */
export function getToolbarMenuMaxHeight(anchorBottom: number, viewportHeight: number): number | undefined {
  const roomBelowAnchor = Math.floor(viewportHeight - anchorBottom - MENU_OFFSET_PX - POPOVER_MARGIN_THRESHOLD_PX);
  return roomBelowAnchor >= MENU_MIN_MAX_HEIGHT_PX ? roomBelowAnchor : undefined;
}

export interface ToolbarMenuProps extends Omit<MenuProps, 'open'>, ToolbarElementProps {
  id: string;
  /**
   * Should be a `Icon`, `SvgIcon` or a `@mui/icons-material` SVG icon element.
   */
  icon?: ReactNode;
  title?: string;
  disabled?: boolean;
  /**
   * The tooltip only shows when no title is defined (i.e. an ToolbarIconButton is being used)
   */
  tooltip?: string;
  /**
   * @default false
   */
  disableCloseOnItemSelect?: boolean;
  menuPaperRef?: React.Ref<HTMLDivElement>;
  toolbarIconButtonStyles?: SxProps;
  toolbarButtonStyles?: SxProps;
  customOnClick?: () => void;
  placement?: TooltipProps['placement'];
}

/**
 * A customized MUI Menu component intended to be used inside the Toolbar component.
 */
export function ToolbarMenu({
  id,
  icon,
  title,
  disabled,
  children,
  hasDivider,
  tooltip,
  disableCloseOnItemSelect = false,
  menuPaperRef,
  toolbarIconButtonStyles,
  toolbarButtonStyles,
  customOnClick,
  placement,
  ...menuProps
}: ToolbarMenuProps) {
  const {PaperProps, onClose: onCloseFromProps, sx: menuSx, ...rest} = menuProps;
  const {anchorEl, isOpen, handleClick, handleClose} = usePopupHandler<HTMLButtonElement>(customOnClick);

  const handleCloseMenu = (event: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    handleClose();
    if (onCloseFromProps) {
      onCloseFromProps(event, reason);
    }
  };

  const [paperMaxHeight, setPaperMaxHeight] = useState<number>();

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Set together with the anchor so the very first render of the open menu is already capped,
    // which is when MUI Popover measures the paper to position it.
    setPaperMaxHeight(getToolbarMenuMaxHeight(event.currentTarget.getBoundingClientRect().bottom, window.innerHeight));
    handleClick(event);
  };

  // MUI Popover re-positions the paper on window resize, so keep the cap in sync while the menu is open
  useEffect(() => {
    if (!isOpen || !anchorEl) {
      return;
    }

    const updateMaxHeight = () => {
      setPaperMaxHeight(getToolbarMenuMaxHeight(anchorEl.getBoundingClientRect().bottom, window.innerHeight));
    };

    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, [isOpen, anchorEl]);

  return (
    <>
      {title ? (
        <ToolbarButton
          id={id}
          data-testid={id}
          disabled={disabled}
          icon={icon}
          title={title}
          onClick={handleOpenMenu}
          aria-controls={isOpen ? id : undefined}
          aria-haspopup='true'
          aria-expanded={isOpen ? 'true' : undefined}
          hasDivider={hasDivider}
          tooltip={tooltip}
          placement={placement}
          sx={toolbarButtonStyles}
        />
      ) : (
        <ToolbarIconButton
          id={id}
          data-testid={id}
          disabled={disabled}
          icon={icon}
          tooltip={tooltip}
          onClick={handleOpenMenu}
          aria-controls={isOpen ? id : undefined}
          aria-haspopup='true'
          aria-expanded={isOpen ? 'true' : undefined}
          hasDivider={hasDivider}
          placement={placement}
          sx={toolbarIconButtonStyles}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        id={id}
        open={isOpen}
        onClose={handleCloseMenu}
        onClick={disableCloseOnItemSelect ? undefined : handleClose}
        disableEnforceFocus
        PaperProps={{ref: menuPaperRef, ...PaperProps, style: {maxHeight: paperMaxHeight, ...PaperProps?.style}}}
        sx={[
          {
            marginTop: 2,
            '& .MuiListItemIcon-root': {
              color: 'text.primary',
            },
          },
          ...(Array.isArray(menuSx) ? menuSx : [menuSx]),
        ]}
        {...rest}
      >
        {children}
      </Menu>
    </>
  );
}
